/**
 * MetricCard - Displays a single metric with optional change indicator and tooltip
 * 
 * Migrated from seo-engine-webtools-customer-module
 */

import { Card, CardContent, Typography, Box, Tooltip, IconButton } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, InfoOutlined } from '@mui/icons-material';
import { formatChange } from '@/services/customer/formatters';

interface Props {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  invertChange?: boolean;
  tooltip?: string;
  onCardClick?: () => void;
}

function MetricCard({ 
  title, 
  value, 
  change, 
  changeLabel, 
  invertChange = false,
  tooltip,
  onCardClick,
}: Props) {
  const isPositive = change !== undefined && (invertChange ? change < 0 : change > 0);

  const TrendIcon = change === undefined || change === 0 
    ? TrendingFlat 
    : isPositive 
      ? TrendingUp 
      : TrendingDown;

  const trendColor = change === undefined || change === 0
    ? 'text.secondary'
    : isPositive
      ? 'success.main'
      : 'error.main';

  return (
    <Card 
      sx={{ 
        cursor: onCardClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onCardClick ? {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        } : {},
      }}
      onClick={onCardClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 0 }}>
            {title}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography variant="h4" component="div">
          {value}
        </Typography>
        {change !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <TrendIcon sx={{ color: trendColor, fontSize: 20, mr: 0.5 }} />
            <Typography variant="body2" sx={{ color: trendColor }}>
              {formatChange(change)}
            </Typography>
            {changeLabel && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {changeLabel}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default MetricCard;
